import React, { useState } from 'react';
import { X, AlertCircle, MapPin, Phone, User, Link2, Map, Package, FileText, Building2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import CopyUrlModal from './CopyUrlModal';
import LocationSelectorModal from './LocationSelectorModal';
import WhatsAppModal from './modals/WhatsAppModal';

const API_URL = process.env.REACT_APP_API_URL;

function MissingLocationModal({ missingPoints, onClose }) {
  const [showCopyUrlModal, setShowCopyUrlModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedPointData, setSelectedPointData] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);

  const handleCopyUrl = (point) => {
    setSelectedPoint(point);
    setShowCopyUrlModal(true);
  };

  const handleSelectLocation = (point) => {
    setSelectedPoint(point);
    setShowLocationModal(true);
  };

  async function handleWhatsApp(point) {
    if (!point.recipient_phone) {
      alert('Este punto no tiene número de teléfono registrado');
      return;
    }
    
    // Set the basic point first (for loading state)
    setSelectedPoint(point);
    setIsFetchingData(true);

    try {
      // Fetch full delivery point data
      const response = await axios.get(
        `${API_URL}/api/admin/delivery-point/${point.id}`
      );
      
      // Set the full data
      setSelectedPointData(response.data);
      
      // Open the WhatsApp modal
      setShowWhatsAppModal(true);
    } catch (error) {
      console.error('Error fetching delivery point data:', error);
      alert('Error al cargar los datos de la entrega. Por favor intenta de nuevo.');
      setSelectedPoint(null);
    } finally {
      setIsFetchingData(false);
    }
  }

  const handleLocationSaved = () => {
    setShowLocationModal(false);
    setSelectedPoint(null);
    // The parent component will receive the update via Socket.IO
  };

  const handleWhatsAppClose = () => {
    setShowWhatsAppModal(false);
    setSelectedPoint(null);
    setSelectedPointData(null);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 font-montserrat" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[85dvh] min-h-0 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-red-600 px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-2 rounded-t-xl shrink-0">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">
                Puntos sin Ubicación
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-red-700 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6">
            {missingPoints.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MapPin className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  ¡Excelente!
                </h3>
                <p className="text-slate-600">
                  Todos los puntos de entrega tienen ubicación asignada
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>{missingPoints.length}</strong> punto(s) de entrega sin coordenadas de ubicación.
                    Estos puntos necesitan ser actualizados con latitud y longitud válidas.
                  </p>
                </div>

                <div className="space-y-3">
                  {missingPoints.map((point) => (
                    <div
                      key={point.id}
                      className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:bg-slate-100 transition-colors"
                    >
                      {/* Header with ID and Actions */}
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                          <span className="bg-red-500 text-white font-bold text-sm px-3 py-1 rounded-lg">
                            #{point.id}
                          </span>
                          {point.sequence && (
                            <span className="bg-slate-200 text-slate-700 text-xs px-2 py-1 rounded">
                              Seq: {point.sequence}
                            </span>
                          )}
                          {point.district && (
                            <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded border border-slate-200">
                              {point.district}
                            </span>
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleWhatsApp(point)}
                            disabled={isFetchingData && selectedPoint?.id === point.id}
                            className="p-1.5 bg-green-100 hover:bg-green-200 disabled:bg-green-50 disabled:cursor-wait text-green-600 rounded-lg transition-colors relative"
                            title="Solicitar ubicación por WhatsApp"
                          >
                            {isFetchingData && selectedPoint?.id === point.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <MessageCircle className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleCopyUrl(point)}
                            className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                            title="Copiar URL"
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleSelectLocation(point)}
                            className="p-1.5 bg-brand-green hover:bg-brand-green/80 text-white rounded-lg transition-colors"
                            title="Seleccionar ubicación"
                          >
                            <Map className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Main Information */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-800">
                            {point.recipient_name || 'Sin nombre'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-slate-600">
                            {point.recipient_phone || 'Sin teléfono'}
                          </span>
                        </div>

                        {point.address && (
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600 flex-1">
                              {point.address}
                            </span>
                          </div>
                        )}

                        {point.product && (
                          <div className="flex items-start gap-2">
                            <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600 flex-1">
                              {point.product}
                            </span>
                          </div>
                        )}

                        {point.notes && (
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600 flex-1 italic">
                              {point.notes}
                            </span>
                          </div>
                        )}

                        {/* User Info */}
                        {point.username && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-sm text-slate-600">
                              Usuario: {point.username} (ID: {point.user_id})
                            </span>
                          </div>
                        )}

                        {/* Driver Info */}
                        {point.driver_id && (
                          <div className="mt-2 pt-2 border-t border-slate-200">
                            <span className="text-xs text-slate-500">
                              Conductor asignado: {point.driver?.username || `ID ${point.driver_id}`}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Missing Coordinates Warning */}
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-2 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3" />
                          <span>
                            {!point.latitude && !point.longitude
                              ? 'Falta latitud y longitud'
                              : !point.latitude
                              ? 'Falta latitud'
                              : 'Falta longitud'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 rounded-b-xl">
            <button
              onClick={onClose}
              className="w-full py-2 px-4 bg-brand-primary hover:bg-brand-primary/90 text-white font-medium rounded-lg transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Copy URL Modal */}
      {showCopyUrlModal && selectedPoint && (
        <CopyUrlModal
          point={selectedPoint}
          onClose={() => {
            setShowCopyUrlModal(false);
            setSelectedPoint(null);
          }}
        />
      )}

      {/* Location Selector Modal */}
      {showLocationModal && selectedPoint && (
        <LocationSelectorModal
          point={selectedPoint}
          onClose={() => {
            setShowLocationModal(false);
            setSelectedPoint(null);
          }}
          onSave={handleLocationSaved}
        />
      )}

      {/* WhatsApp Modal - Only show when we have full data */}
      {showWhatsAppModal && selectedPointData && (
        <WhatsAppModal
          item={selectedPointData}
          itemType="delivery"
          onClose={handleWhatsAppClose}
          userId={2} // Admin user ID
        />
      )}
    </>
  );
}

export default MissingLocationModal;
